import { t as uiText } from '../i18n';
/** @jsxImportSource react */
import { getFeatureFlags } from '../config/featureFlags';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { analyzeForSearch } from '../api/sudachi';
import { executeAdminRAGStream } from '../api/adminRagStream';
import { RAGStreamRequest, AUX_REFINED_FAILURE_MESSAGE } from '../api/ragStream';
import { testEmbeddingData } from '../api/debug';
import { runVectorSearchTest } from '../api/vectorSearchTest';
import { getCloudRunEndpoint } from '../config/cloudRunConfig';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import DelayedTooltip from './DelayedTooltip';
import { AdminWhisperProvider } from '../features/speech/AdminWhisperProvider';
import { useImeCompositionGuard } from '../hooks/useImeCompositionGuard';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';
import { updateCrawlReferencedUrls } from '../features/workspace/updateWorkspaceSettings';
import { isNcgSeries } from '../features/plans/series';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { db } from '../lib/firebase';
import './RAGSearchInterface.css';

interface SessionHistoryEntry {
  sessionId: string;
  title: string | null;
  createdAt: string | null;
}

// ハイライト用ユーティリティ

type RagErrorCode =
  | 'FETCH_ERROR'
  | 'NETWORK'
  | 'STREAM_ERROR'
  | 'INVALID_REQUEST'
  | 'INVALID_PUBLISH_ID'
  | 'PAGE_NOT_FOUND'
  | 'RATE_LIMITED'
  | 'WORKSPACE_MISMATCH'
  | 'FIRESTORE_ERROR'
  | 'CONFIG_ERROR'
  | 'LLM_ERROR'
  | 'INTERNAL_ERROR'
  | 'ERROR';

type RagHandledError = Error & {
  code?: RagErrorCode | string;
};

function getBilingualRagErrorMessage(code?: string): string {
  switch (code) {
    case 'FETCH_ERROR':
    case 'NETWORK':
    case 'STREAM_ERROR':
      return [
        'A communication error occurred. Please check your network connection and try again in a few moments.',
        uiText("A network error occurred. Check your connection and try again in a moment.")
      ].join('\n');
    case 'INVALID_REQUEST':
    case 'INVALID_PUBLISH_ID':
    case 'PAGE_NOT_FOUND':
      return [
        'We could not verify the input or page information. Please review the content and try again.',
        uiText("Couldn't verify the input or the page. Check the content and try again.")
      ].join('\n');
    case 'RATE_LIMITED':
    case 'WORKSPACE_MISMATCH':
      return [
        'This action is currently unavailable. Please wait a while or check your access permissions.',
        uiText("This action cannot be run right now. Wait a moment, or check your access.")
      ].join('\n');
    case 'FIRESTORE_ERROR':
    case 'CONFIG_ERROR':
    case 'LLM_ERROR':
    case 'INTERNAL_ERROR':
      return [
        "We're currently experiencing a service issue. Please try again in a few moments.",
        uiText("There is a problem on our side. Please try again later.")
      ].join('\n');
    default:
      return [
        'An error occurred. Please try again in a few moments.',
        uiText("An error occurred. Please try again later.")
      ].join('\n');
  }
}

function createRagHandledError(code?: string, message?: string): RagHandledError {
  const error = new Error(message || 'Unknown error') as RagHandledError;
  error.code = code;
  return error;
}

function getOrCreateAdminRAGSessionId(workspaceId: string): string {
  if (typeof window === 'undefined') {
    return `admin_rag_${workspaceId}_${Date.now()}`;
  }

  const storageKey = `notreu_admin_rag_session:${workspaceId}`;
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }

  const sessionId =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  window.sessionStorage.setItem(storageKey, sessionId);
  return sessionId;
}

function collectHighlightRanges(text: string, tokens: string[]): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  if (!text || tokens.length === 0) return ranges;

  const lower = text.toLowerCase();
  const uniqueTokens = Array.from(new Set(tokens.map(t => t.trim().toLowerCase()).filter(t => t.length > 1)));
  for (const t of uniqueTokens) {
    const needle = t;
    let from = 0;
    while (true) {
      const idx = lower.indexOf(needle, from);
      if (idx === -1) break;
      ranges.push({ start: idx, end: idx + needle.length });
      from = idx + needle.length;
      if (ranges.length > 512) break; // 安全上限
    }
    if (ranges.length > 512) break;
  }

  // マージ（重複/交差）
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Array<{ start: number; end: number }> = [];
  for (const r of ranges) {
    if (merged.length === 0) {
      merged.push({ ...r });
    } else {
      const last = merged[merged.length - 1];
      if (r.start <= last.end) {
        last.end = Math.max(last.end, r.end);
      } else {
        merged.push({ ...r });
      }
    }
  }
  return merged;
}

function buildSnippetWindow(text: string, ranges: Array<{ start: number; end: number }>, maxLen = 200): { start: number; end: number } {
  if (!text) return { start: 0, end: 0 };
  if (text.length <= maxLen) return { start: 0, end: text.length };
  if (!ranges || ranges.length === 0) return { start: 0, end: maxLen };
  const first = ranges[0];
  const context = Math.max(0, Math.floor((maxLen - (first.end - first.start)) / 2));
  const start = Math.max(0, first.start - context);
  const end = Math.min(text.length, start + maxLen);
  return { start, end };
}

function renderHighlighted(text: string, tokens: string[], maxLen = 200): React.ReactNode {
  if (!text) return null;
  const ranges = collectHighlightRanges(text, tokens);
  const window = buildSnippetWindow(text, ranges, maxLen);
  const visible = text.slice(window.start, window.end);

  if (ranges.length === 0) {
    const tail = text.length > window.end ? '…' : '';
    const head = window.start > 0 ? '…' : '';
    return <>{head}{visible}{tail}</>;
  }

  // 可視範囲内のレンジへ変換
  const adjusted: Array<{ start: number; end: number }> = [];
  for (const r of ranges) {
    if (r.end <= window.start) continue;
    if (r.start >= window.end) break;
    adjusted.push({ start: Math.max(0, r.start - window.start), end: Math.min(window.end - window.start, r.end - window.start) });
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const r of adjusted) {
    if (cursor < r.start) {
      parts.push(visible.slice(cursor, r.start));
    }
    parts.push(<em key={`hl-${key++}`}>{visible.slice(r.start, r.end)}</em>);
    cursor = r.end;
  }
  if (cursor < visible.length) parts.push(visible.slice(cursor));

  const head = window.start > 0 ? '…' : '';
  const tail = text.length > window.end ? '…' : '';
  return <>{head}{parts}{tail}</>;
}

// ---- Markdown インライン要素（太字・斜体・コード・プレーンURL）を React ノードへ変換 ----
// renderWithLinks と renderMarkdownTable の両方から呼ばれるためモジュール関数に切り出す。
function renderMarkdownInline(segment: string, baseKey: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let partKey = 0;
  let processed = segment;

  processed = processed.replace(/\*\*(.*?)\*\*/g, (_match, content) => {
    const ph = `__BOLD_${partKey++}__`;
    parts.push(<strong key={`${baseKey}-b${partKey}`}>{content}</strong>);
    return ph;
  });

  processed = processed.replace(/\*(.*?)\*/g, (_match, content) => {
    const ph = `__ITALIC_${partKey++}__`;
    parts.push(<em key={`${baseKey}-i${partKey}`}>{content}</em>);
    return ph;
  });

  processed = processed.replace(/`(.*?)`/g, (_match, content) => {
    const ph = `__CODE_${partKey++}__`;
    parts.push(<code key={`${baseKey}-c${partKey}`}>{content}</code>);
    return ph;
  });

  processed = processed.replace(/(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/g, (_match, url) => {
    const ph = `__URL_${partKey++}__`;
    parts.push(
      <a key={`${baseKey}-u${partKey}`} href={url} target="_blank" rel="noopener noreferrer" aria-label={uiText("External link: {{url}}", { url: url })}>
        {url}
      </a>
    );
    return ph;
  });

  const finalParts: React.ReactNode[] = [];
  const textParts = processed.split(/(__\w+_\d+__)/);
  let placeholderIndex = 0;
  for (const part of textParts) {
    if (part.match(/^__\w+_\d+__$/)) {
      if (placeholderIndex < parts.length) {
        finalParts.push(parts[placeholderIndex]);
        placeholderIndex++;
      }
    } else if (part) {
      finalParts.push(<span key={`${baseKey}-t${partKey++}`}>{part}</span>);
    }
  }
  return <>{finalParts}</>;
}

// ---- Markdown パイプ表を <table> React 要素へ変換 ----
// 区切り行（| --- | など）を含まない行グループは null を返してプレーンテキスト扱いに戻す。
// これによりストリーミング途中の不完全な表を誤変換しない。
function renderMarkdownTable(tableLines: string[], tableKey: string): React.ReactNode | null {
  // 区切り行: | と - と : と空白だけで構成される行
  const sepRegex = /^\|[\s|:=-]+\|$/;
  const sepIndex = tableLines.findIndex(l => sepRegex.test(l));
  if (sepIndex < 1) return null; // 区切り行がない、またはヘッダ行がない

  const parseRow = (line: string): string[] =>
    line
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());

  const headerCells = parseRow(tableLines[sepIndex - 1]);
  if (headerCells.length === 0) return null;

  const bodyLines = tableLines.slice(sepIndex + 1).filter(l => l.trim().length > 0);

  return (
    <table key={tableKey}>
      <thead>
        <tr>
          {headerCells.map((cell, ci) => (
            <th key={`${tableKey}-h${ci}`}>
              {renderMarkdownInline(cell, `${tableKey}-h${ci}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyLines.map((line, ri) => {
          const cells = parseRow(line);
          // セル数がヘッダと合わない行は末尾を空セルで埋める
          while (cells.length < headerCells.length) cells.push('');
          return (
            <tr key={`${tableKey}-r${ri}`}>
              {cells.slice(0, headerCells.length).map((cell, ci) => (
                <td key={`${tableKey}-r${ri}c${ci}`}>
                  {renderMarkdownInline(cell, `${tableKey}-r${ri}c${ci}`)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- テキストを「表ブロック」と「非表テキスト」に分割 ----
type RagTextBlock =
  | { type: 'text'; content: string }
  | { type: 'table'; lines: string[] };

function splitIntoRagBlocks(rawText: string): RagTextBlock[] {
  const lines = rawText.split('\n');
  const blocks: RagTextBlock[] = [];
  let textBuffer: string[] = [];
  let tableBuffer: string[] = [];

  const flushText = () => {
    if (textBuffer.length > 0) {
      blocks.push({ type: 'text', content: textBuffer.join('\n') });
      textBuffer = [];
    }
  };
  const flushTable = () => {
    if (tableBuffer.length > 0) {
      blocks.push({ type: 'table', lines: tableBuffer });
      tableBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    // 先頭と末尾が | で長さ 3 以上（| x | 以上）の行を表行と判断
    const isTableRow = trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length >= 3;
    if (isTableRow) {
      flushText();
      tableBuffer.push(trimmed);
    } else {
      flushTable();
      textBuffer.push(line);
    }
  }
  flushText();
  flushTable();
  return blocks;
}

// テキスト中の [label](url) とプレーンURLを a タグに変換（React要素で安全に描画）
// Markdown画像 ![alt](url)、太字・斜体・コード、Markdown表にも対応
function renderWithLinks(rawText: string): React.ReactNode {
  const blocks = splitIntoRagBlocks(rawText);
  const topElements: React.ReactNode[] = [];
  let blockIdx = 0;

  for (const block of blocks) {
    if (block.type === 'table') {
      const tableNode = renderMarkdownTable(block.lines, `tbl-${blockIdx}`);
      if (tableNode !== null) {
        topElements.push(tableNode);
        blockIdx++;
        continue;
      }
      // 区切り行なし → 以下の非表処理に fallthrough させるため content を組み立てる
    }

    // 非表ブロック（または表と判断できなかったパイプ行）: 既存のリンク・画像処理
    const blockText = block.type === 'text' ? block.content : block.lines.join('\n');
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    // ブロックインデックスをキーのプレフィクスに含めて兄弟間の一意性を確保
    let key = blockIdx * 100000;

    const renderMarkdown = (segment: string): React.ReactNode =>
      renderMarkdownInline(segment, `b${blockIdx}k${key}`);

    const combinedRegex = /(!?\[([^\]]*)\]\((https?:\/\/[^\s)]+|tel:[^\s)]+)\))/g;

    let match: RegExpExecArray | null;
    while ((match = combinedRegex.exec(blockText)) !== null) {
      const before = blockText.slice(lastIndex, match.index);
      if (before) {
        elements.push(<span key={`md-${key++}`}>{renderMarkdown(before)}</span>);
      }

      const fullMatch = match[1];
      const isImage = fullMatch.startsWith('!');
      const label = match[2];
      const url = match[3];

      if (isImage) {
        elements.push(
          <div key={`img-${key++}`} style={{
            margin: '16px 0',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            maxWidth: '100%'
          }}>
            <img
              src={url}
              alt={label || uiText("Image")}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                maxHeight: '500px'
              }}
              loading="lazy"
            />
            {label && (
              <div style={{
                padding: '8px 12px',
                fontSize: '14px',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb'
              }}>
                {label}
              </div>
            )}
          </div>
        );
      } else {
        const isTelLink = url.startsWith('tel:');
        elements.push(
          <a
            key={`a-${key++}`}
            href={url}
            target={isTelLink ? undefined : "_blank"}
            rel={isTelLink ? undefined : "noopener noreferrer"}
            aria-label={isTelLink ? uiText("Call: {{label}}", { label: label }) : uiText("External link: {{label}}", { label: label })}
          >
            {label}
          </a>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    const tail = blockText.slice(lastIndex);
    if (tail) {
      elements.push(<span key={`tail-${key++}`}>{renderMarkdown(tail)}</span>);
    }

    topElements.push(<React.Fragment key={`blk-${blockIdx}`}>{elements}</React.Fragment>);
    blockIdx++;
  }

  return <>{topElements}</>;
}

// SVGアイコンコンポーネント
const SendIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
  </svg>
);

const MicIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zm7-3a7 7 0 01-14 0m7 7v3m-4 0h8" />
  </svg>
);

const StopIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6h12v12H6z" />
  </svg>
);

const MicFlowVisualizer = React.memo(
  ({ active, levelRef }: { active: boolean; levelRef: React.MutableRefObject<number> }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const lastPushMsRef = useRef<number>(0);
    const ringRef = useRef<number[]>([]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const prefersReduced =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const cssHeight = 24;
      const dpr = typeof window !== 'undefined' ? Math.max(1, Math.min(2, window.devicePixelRatio || 1)) : 1;
      const step = 4;
      const dotSize = 2;
      const barW = 2;
      const midY = cssHeight / 2;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const applySize = (cssWidth: number) => {
        const w = Math.max(40, Math.floor(cssWidth));
        canvas.style.width = `${w}px`;
        canvas.style.height = `${cssHeight}px`;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(cssHeight * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const slots = Math.max(12, Math.floor(w / step));
        if (ringRef.current.length !== slots) {
          ringRef.current = new Array(slots).fill(0);
        }
      };

      const parent = canvas.parentElement;
      applySize(parent ? parent.clientWidth : 140);

      let ro: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined' && parent) {
        ro = new ResizeObserver(() => applySize(parent.clientWidth));
        ro.observe(parent);
      }

      const draw = () => {
        const cssWidth = Math.max(40, Math.floor(canvas.clientWidth || 140));
        ctx.clearRect(0, 0, cssWidth, cssHeight);
        ctx.fillStyle = 'rgba(203, 213, 225, 0.7)';
        const slots = ringRef.current.length;
        for (let i = 0; i < slots; i++) {
          const x = i * step + Math.floor(step / 2);
          ctx.fillRect(x - dotSize / 2, midY - dotSize / 2, dotSize, dotSize);
        }
        ctx.fillStyle = 'rgba(51, 65, 85, 0.85)';
        for (let i = 0; i < ringRef.current.length; i++) {
          const v = ringRef.current[i] || 0;
          if (v <= 0.01) continue;
          const vv = Math.min(1, v * 1.5);
          const h = Math.min(cssHeight / 2 - 1, 1 + vv * (cssHeight / 2 - 2));
          const x = i * step + Math.floor(step / 2) - barW / 2;
          ctx.fillRect(x, midY - h, barW, h * 2);
        }
      };

      const tick = (t: number) => {
        if (!active) { draw(); return; }
        const pushIntervalMs = prefersReduced ? 220 : 33;
        if (!lastPushMsRef.current) lastPushMsRef.current = t;
        if (t - lastPushMsRef.current >= pushIntervalMs) {
          lastPushMsRef.current = t;
          const arr = ringRef.current;
          for (let i = 0; i < arr.length - 1; i++) arr[i] = arr[i + 1];
          arr[arr.length - 1] = Math.max(0, Math.min(1, levelRef.current || 0));
        }
        draw();
        rafRef.current = requestAnimationFrame(tick);
      };

      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      lastPushMsRef.current = 0;
      if (active) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        draw();
      }

      return () => {
        if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (ro && parent) { try { ro.disconnect(); } catch {} }
      };
    }, [active, levelRef]);

    return (
      <div className="mic-flow-viz" aria-hidden="true">
        <canvas ref={canvasRef} />
      </div>
    );
  }
);

const LoaderIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DollarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

/**
 * RAG検索結果の型定義
 */
interface RAGSource {
  boardId: string;
  chunkId: string;
  text: string;
  similarityScore: number;
  metadata?: {
    title?: string;
    lastModified?: string;
    tags?: string[];
  };
}

interface RAGResponse {
  success: boolean;
  answer: string;
  sources: RAGSource[];
  confidence: number;
  processingTime: number;
  usage: {
    vectorSearchTime: number;
    llmGenerationTime: number;
    totalTokens: number;
    estimatedCost: number;
  };
  guardrails: {
    triggered: boolean;
    reason?: string;
    fallbackUsed: boolean;
  };
  metadata: {
    queryHash: string;
    model: string;
    searchResults: number;
    contextLength: number;
  };
}

/**
 * 参照 URL の鮮度チェック結果（サーバー側 checkReferencedUrls の返り値と一致）
 */
interface FreshnessCheckItem {
  url: string;
  targetId: string | null;
  status: 'fresh' | 'stale' | 'unknown';
  lastCrawledAt?: string;
  method?: 'etag' | 'last-modified' | 'not-registered' | 'no-metadata' | 'head-failed' | 'head-not-found';
  reason?: string;
}

/**
 * チャット履歴の型定義
 */
interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  ragResponse?: RAGResponse;
  isLoading?: boolean;
  mode?: 'answer' | 'search';
  /** 参照 URL 鮮度チェック結果（サーバーから freshness SSE イベントで届く） */
  freshness?: FreshnessCheckItem[];
  /**
   * 案 A: 参照 URL 補助データ 2-pass の状態と結果（補足セクション追加型）
   * - 'pending' : answer SSE で auxRefinePending=true を受信、answer_refined 待ち
   * - 'ready'   : answer_refined status='ready' 受信、refinedContent に補足フル回答
   * - 'failed'  : answer_refined status='failed' 受信 or complete までに来なかった
   */
  refinedStatus?: 'pending' | 'ready' | 'failed';
  refinedContent?: string;
  refinedFailureMessage?: string;
  refinedFromUrls?: string[];
}

/**
 * RAG検索インターフェースのProps
 */
interface RAGSearchInterfaceProps {
  onError?: (error: string) => void;
  className?: string;
  externalNewChatRequest?: number;
  externalSessionSelection?: {
    sessionId: string;
    requestId: number;
  } | null;
  onSessionHistoryTitleResolved?: (entry: { sessionId: string; title: string }) => void;
  onSessionHistoryRevalidate?: () => void;
}

/**
 * RAG検索インターフェースコンポーネント
 */
export const RAGSearchInterface: React.FC<RAGSearchInterfaceProps> = ({
  onError,
  className = '',
  externalNewChatRequest = 0,
  externalSessionSelection = null,
  onSessionHistoryTitleResolved,
  onSessionHistoryRevalidate,
}) => {
  const { workspaceId = '' } = useParams();
  const { selectedWorkspace } = useWorkspaceContext();
  const { data: isAdmin = false } = useIsWorkspaceAdminQuery(workspaceId, !!workspaceId);
  const showReferencedUrlFollowUp =
    isAdmin &&
    selectedWorkspace?.id === workspaceId &&
    !isNcgSeries(selectedWorkspace.seriesId);
  const [crawlReferencedUrls, setCrawlReferencedUrls] = useState(false);
  const [crawlReferencedUrlsSaving, setCrawlReferencedUrlsSaving] = useState(false);
  const crawlReferencedUrlsTouchedRef = useRef(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<'answer' | 'search'>('answer');
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState<'up' | 'down'>('up');
  const modeDropdownRef = useRef<HTMLDivElement>(null);

  // 🎙️ 音声入力（Whisper）
  const sttProviderRef = useRef<AdminWhisperProvider | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [sttError, setSttError] = useState<string>('');
  const micLevelRef = useRef<number>(0);
  const [showSources, setShowSources] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [vectorTestResult, setVectorTestResult] = useState<any>(null);
  const [showVectorTest, setShowVectorTest] = useState(false);
  const [streamingProgress, setStreamingProgress] = useState(0);
  const [streamingStatus, setStreamingStatus] = useState('');
  const [showMenuOptions, setShowMenuOptions] = useState(true);
  // 初期レイアウト（入力欄を中央）かどうかを管理
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  // Home画面ではストリーミングを固定ON（公開版の見た目/条件に揃えるためのフラグ）
  const useStreaming = true;
  const { onCompositionStart, onCompositionEnd, shouldBlockEnterSubmit } =
    useImeCompositionGuard();

  // セッション管理
  const [sessionId, setSessionId] = useState(() => getOrCreateAdminRAGSessionId(workspaceId));
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentSessionTitle, setCurrentSessionTitle] = useState<string | null>(null);
  const lastHandledExternalNewChatRequestRef = useRef(externalNewChatRequest);
  const lastHandledExternalSessionSelectionRef = useRef(externalSessionSelection?.requestId ?? 0);
  const pendingSessionTitleTimeoutRef = useRef<number | null>(null);
  const pendingSessionTitleSessionIdRef = useRef<string | null>(null);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(e.target as Node)) {
        setModeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!workspaceId) {
      setCrawlReferencedUrls(false);
      crawlReferencedUrlsTouchedRef.current = false;
      return;
    }
    crawlReferencedUrlsTouchedRef.current = false;
    let cancelled = false;
    const load = async () => {
      try {
        const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
        if (cancelled || crawlReferencedUrlsTouchedRef.current) return;
        setCrawlReferencedUrls(wsSnap.exists() && wsSnap.data()?.crawlReferencedUrls === true);
      } catch (error) {
        console.error('内部 RAG の参照 URL 補足設定の読み込みに失敗:', error);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const handleCrawlReferencedUrlsToggle = async () => {
    if (!workspaceId || !showReferencedUrlFollowUp || crawlReferencedUrlsSaving) return;
    const next = !crawlReferencedUrls;
    crawlReferencedUrlsTouchedRef.current = true;
    setCrawlReferencedUrls(next);
    setCrawlReferencedUrlsSaving(true);
    try {
      await updateCrawlReferencedUrls(workspaceId, next);
    } catch (error) {
      setCrawlReferencedUrls(!next);
      console.error('内部 RAG の参照 URL 補足設定の更新に失敗:', error);
      alert(uiText("Failed to update referenced URL follow-up settings"));
    } finally {
      setCrawlReferencedUrlsSaving(false);
    }
  };

  // 🎙️ 音声入力: コンポーネントアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      try { sttProviderRef.current?.dispose(); } catch {}
      sttProviderRef.current = null;
    };
  }, []);

  const startWhisper = async () => {
    const TAG = '🎙️ [Admin RAG Voice]';
    if (!workspaceId) {
      console.error(`${TAG} ❌ workspaceId が未設定のため音声入力不可`);
      return;
    }
    if (isLoading || isRecording || isTranscribing) {
      console.warn(`${TAG} startWhisper() 無視 (isLoading=${isLoading} isRecording=${isRecording} isTranscribing=${isTranscribing})`);
      return;
    }
    console.log(`${TAG} 音声入力開始 workspaceId=${workspaceId}`);
    setSttError('');
    micLevelRef.current = 0;
    const provider = new AdminWhisperProvider({ workspaceId, language: 'ja' });
    provider.onLevel((level01) => {
      micLevelRef.current = level01;
    });
    provider.onTranscript((text, isFinal) => {
      if (!isFinal) return;
      console.log(`${TAG} ✅ 文字起こし受信: "${text}"`);
      setIsTranscribing(false);
      setIsRecording(false);
      setInputValue((prev) => {
        const p = prev ?? '';
        return !p.trim() ? text : `${p}${p.endsWith('\n') ? '' : '\n'}${text}`;
      });
      try { inputRef.current?.focus(); } catch {}
    });
    provider.onError((e) => {
      if (e.code === 'NO_SPEECH') {
        console.warn(`${TAG} ⚠️ 無音のためスキップ`);
      } else {
        console.error(`${TAG} ❌ エラー: code=${e.code} message=${e.message}`);
        setSttError(e.message);
      }
      setIsTranscribing(false);
      setIsRecording(false);
      try { provider.dispose(); } catch {}
    });
    sttProviderRef.current = provider;
    await provider.start();
    const state = provider.getState();
    console.log(`${TAG} provider.start() 完了 state=${state}`);
    setIsRecording(state === 'recording');
  };

  const stopWhisper = async () => {
    const TAG = '🎙️ [Admin RAG Voice]';
    const provider = sttProviderRef.current;
    if (!provider || !isRecording) {
      console.warn(`${TAG} stopWhisper() 無視 (provider=${!!provider} isRecording=${isRecording})`);
      return;
    }
    console.log(`${TAG} 録音停止リクエスト`);
    setIsRecording(false);
    setIsTranscribing(true);
    micLevelRef.current = 0;
    await provider.stop();
    sttProviderRef.current = null;
    console.log(`${TAG} stopWhisper() 完了`);
  };

  // メッセージ追加時に自動スクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 入力フィールドにフォーカス
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const clearPendingSessionTitleTimeout = useCallback(() => {
    if (pendingSessionTitleTimeoutRef.current != null) {
      window.clearTimeout(pendingSessionTitleTimeoutRef.current);
      pendingSessionTitleTimeoutRef.current = null;
    }
    pendingSessionTitleSessionIdRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      clearPendingSessionTitleTimeout();
    };
  }, [clearPendingSessionTitleTimeout]);

  // テキストエリアの高さを自動調整
  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const maxHeight = 200;
    textarea.style.height = '0px';
    const scrollH = textarea.scrollHeight;
    textarea.style.height = `${Math.min(scrollH, maxHeight)}px`;
    textarea.style.overflowY = scrollH > maxHeight ? 'auto' : 'hidden';
  }, [inputValue]);

  // ワークスペース切り替え時に状態をリセット
  useEffect(() => {
    if (workspaceId) {
      setMessages([]);
      setInputValue('');
      setIsLoading(false);
      setDebugInfo(null);
      setShowDebug(false);
      setVectorTestResult(null);
      setShowVectorTest(false);
      setStreamingProgress(0);
      setStreamingStatus('');
      setHasSentFirstMessage(false);
      setSessionId(getOrCreateAdminRAGSessionId(workspaceId));
      setSessionHistory([]);
      setCurrentSessionTitle(null);
      
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [workspaceId]);

  /**
   * セッション一覧をAPIから取得する
   */
  const fetchSessionList = useCallback(async (currentChildId?: string) => {
    try {
      const auth = getAuth();
      const user = auth?.currentUser;
      if (!user || !workspaceId) return;
      const idToken = await user.getIdToken();
      const baseUrl = `${getCloudRunEndpoint('ragStream')}/session-list`;
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ workspaceId, userId: user.uid }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const sessions: SessionHistoryEntry[] = (data.sessions || [])
        .filter((s: any) => s.sessionId !== (currentChildId || sessionId) && (s.turnCount ?? 0) > 0)
        .map((s: any) => ({ sessionId: s.sessionId, title: s.title || null, createdAt: s.createdAt || null }));
      setSessionHistory(sessions);
    } catch (err) {
      console.error('❌ fetchSessionList failed:', err);
    }
  }, [workspaceId, sessionId]);

  /**
   * 新しいチャットを開始する
   */
  const handleNewSession = useCallback(() => {
    if (!workspaceId) return;
    if (isLoading) return;
    if (!hasSentFirstMessage && messages.length === 0) return;
    const newSessionId =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const storageKey = `notreu_admin_rag_session:${workspaceId}`;
    window.sessionStorage.setItem(storageKey, newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
    setCurrentSessionTitle(null);
    setHasSentFirstMessage(false);
    fetchSessionList(newSessionId);
  }, [workspaceId, isLoading, hasSentFirstMessage, messages.length, fetchSessionList]);

  useEffect(() => {
    if (externalNewChatRequest === lastHandledExternalNewChatRequestRef.current) {
      return;
    }

    lastHandledExternalNewChatRequestRef.current = externalNewChatRequest;
    handleNewSession();
  }, [externalNewChatRequest, handleNewSession]);

  /**
   * 過去のセッションに切り替える
   */
  const handleSwitchSession = useCallback(async (targetSessionId: string) => {
    if (!targetSessionId || targetSessionId === sessionId) return;
    if (isLoading || isLoadingHistory) return;
    setIsLoadingHistory(true);
    try {
      const auth = getAuth();
      const user = auth?.currentUser;
      if (!user || !workspaceId) return;
      const idToken = await user.getIdToken();
      const baseUrl = `${getCloudRunEndpoint('ragStream')}/session-chats`;
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ workspaceId, userId: user.uid, sessionId: targetSessionId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const turns = data.turns || [];

      const restoredMessages: ChatMessage[] = turns.flatMap((turn: any, i: number) => [
        {
          id: `user-restored-${i}`,
          type: 'user' as const,
          content: turn.userMessage,
          timestamp: new Date(),
        },
        {
          id: `assistant-restored-${i}`,
          type: 'assistant' as const,
          content: turn.assistantMessage,
          timestamp: new Date(),
          isLoading: false,
        },
      ]);

      setMessages(restoredMessages);
      setHasSentFirstMessage(restoredMessages.length > 0);

      const targetEntry = sessionHistory.find(e => e.sessionId === targetSessionId);
      setCurrentSessionTitle(targetEntry?.title || null);

      const storageKey = `notreu_admin_rag_session:${workspaceId}`;
      window.sessionStorage.setItem(storageKey, targetSessionId);
      setSessionId(targetSessionId);

      fetchSessionList(targetSessionId);
    } catch (err) {
      console.error('❌ Session switch failed:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [isLoading, isLoadingHistory, workspaceId, sessionId, sessionHistory, fetchSessionList]);

  useEffect(() => {
    if (!externalSessionSelection) return;
    if (externalSessionSelection.requestId === lastHandledExternalSessionSelectionRef.current) {
      return;
    }
    if (isLoading || isLoadingHistory) {
      return;
    }

    lastHandledExternalSessionSelectionRef.current = externalSessionSelection.requestId;
    handleSwitchSession(externalSessionSelection.sessionId);
  }, [externalSessionSelection, handleSwitchSession, isLoading, isLoadingHistory]);

  /**
   * デバッグ: embeddingデータ確認
   */
  const handleDebugEmbedding = async () => {
    if (!workspaceId) {
      onError?.(uiText("No workspace is selected"));
      return;
    }
    
    try {
      setIsLoading(true);
      const result = await testEmbeddingData({ workspaceId });
      setDebugInfo(result);
      setShowDebug(true);
    } catch (error) {
      console.error('❌ [Debug] エラー:', error);
      onError?.(uiText("Debug error: {{error}}", { error: error }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Vector Searchテスト
   */
  const handleVectorSearchTest = async () => {
    if (!workspaceId) {
      onError?.(uiText("No workspace is selected"));
      return;
    }
    
    try {
      setIsLoading(true);
      const result = await runVectorSearchTest(workspaceId);
      setVectorTestResult(result);
      setShowVectorTest(true);
    } catch (error) {
      console.error('❌ [Vector Test] エラー:', error);
      onError?.(uiText("Vector search test error: {{error}}", { error: error }));
    } finally {
      setIsLoading(false);
    }
  };


  /**
   * メッセージ送信処理
   */
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    // ワークスペースIDが設定されていない場合はエラー
    if (!workspaceId) {
      onError?.(uiText("No workspace is selected"));
      return;
    }

    const trimmedInput = inputValue.trim();
    const isFirstMessageInSession = !hasSentFirstMessage && messages.length === 0;
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: trimmedInput,
      timestamp: new Date()
    };

    const loadingMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
      mode: searchMode,
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingProgress(0);
    setStreamingStatus('');

    try {
      // 初回送信時に通常レイアウトへ切り替え
      if (!hasSentFirstMessage) {
        setHasSentFirstMessage(true);
      }

      let effectiveSessionId = sessionId;

      if (isFirstMessageInSession) {
        // 「新規チャット状態」の最初の送信は常に新しい sessionId で開始する。
        // sessionStorage から復元された既存セッション ID をそのまま使うと、
        // 履歴上の既存セッションを上書きしてしまうため。
        const freshSessionId =
          typeof window.crypto?.randomUUID === 'function'
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        if (workspaceId) {
          const storageKey = `notreu_admin_rag_session:${workspaceId}`;
          window.sessionStorage.setItem(storageKey, freshSessionId);
        }
        setSessionId(freshSessionId);
        effectiveSessionId = freshSessionId;

        // SSE が届かない場合のフォールバックとして revalidate タイマーを維持
        clearPendingSessionTitleTimeout();
        pendingSessionTitleSessionIdRef.current = effectiveSessionId;
        pendingSessionTitleTimeoutRef.current = window.setTimeout(() => {
          if (pendingSessionTitleSessionIdRef.current === effectiveSessionId) {
            onSessionHistoryRevalidate?.();
          }
        }, 10000);
      }
      
      // Cloud Run版ストリーミング処理のみ使用
      await handleStreamingMessage(userMessage.content, loadingMessage.id, effectiveSessionId);

    } catch (error) {
      const errorCode =
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof (error as { code?: unknown }).code === 'string'
          ? String((error as { code?: string }).code)
          : undefined;
      const errorMessage: ChatMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: getBilingualRagErrorMessage(errorCode),
        timestamp: new Date(),
        isLoading: false
      };

      setMessages(prev => 
        prev.map(msg => 
          msg.id === loadingMessage.id ? errorMessage : msg
        )
      );

      onError?.(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      // 確実にローディング状態をリセット
      setIsLoading(false);
      
      // ストリーミング状態のリセットは少し遅延させる（完了表示のため）
      setTimeout(() => {
        setStreamingProgress(0);
        setStreamingStatus('');
      }, 1500);
    }
  };

  /**
   * ストリーミングメッセージ処理
   */
  const handleStreamingMessage = async (query: string, messageId: string, overrideSessionId?: string) => {
    const effectiveSessionId = overrideSessionId ?? sessionId;
    let currentContent = '';
    let sources: any[] = [];
    let finalRagResponse: any = null;
    let terminalError: RagHandledError | null = null;
    // フレーム単位で更新を集約
    let rafId: number | null = null;

    // モデル推定と理由の収集（サーバーはモデル名をSSEで返さないため推測する）
    let inferredModel: string = 'gpt-5-mini';
    const modelEvidence: string[] = [];

    // Sudachiで検索最適化（失敗時はフォールバック）
    let sudachiKeywords: string[] | null = null;
    let sudachiUsed = false;
    let normalizedText: string | null = null;
    const { sudachiEnabled } = getFeatureFlags();
    if (sudachiEnabled) {
      try {
        if (workspaceId) {
          const a = await analyzeForSearch({ text: query, workspaceId, mode: 'auto', normalize: true, filters: { minLen: 2 } });
          sudachiKeywords = a.keywords;
          sudachiUsed = Array.isArray(a.keywords) && a.keywords.length > 0;
          normalizedText = a.normalizedText;
        }
      } catch (e) {
        console.warn('Sudachi analyze fallback:', e);
      }
    }

    const preferredLocales =
      typeof navigator !== 'undefined'
        ? navigator.languages?.length
          ? [...navigator.languages]
          : [navigator.language]
        : [];

    const request: RAGStreamRequest = {
      query,
      workspaceId,
      sessionId: effectiveSessionId,
      maxResults: searchMode === 'search' ? 10 : 5,
      minSimilarityScore: 0.25,
      includeContext: true,
      mode: searchMode,
      responseStyle: searchMode === 'answer' ? 'conversational' : 'detailed',
      sudachiKeywords: sudachiKeywords || undefined,
      normalizedQuery: normalizedText || undefined,
      preferredLocales,
    };

    await executeAdminRAGStream(request, {
      onConnected: (data) => {
        setStreamingStatus(uiText("Connected"));
        setStreamingProgress((prev) => (prev === 0 ? 5 : prev));
      },

      onStatus: (data) => {
        // モデル名を含む可能性のあるフォールバックメッセージはUIに出さない
        const safeMessage = data.step === 'fallback' ? uiText("Regenerating...") : data.message;
        setStreamingStatus(safeMessage);
        setStreamingProgress(data.progress);

        // モデル推定: フォールバックイベントに含まれるモデル名を取得
        if (data.step === 'fallback' && typeof data.message === 'string') {
          const candidate = String(data.message).split(' ')[0];
          if (candidate) {
            inferredModel = candidate;
            modelEvidence.push(`server_status_fallback:${candidate}`);
          }
        }
        if (data.step === 'llm_generation') {
          modelEvidence.push('llm_generation_started:assume_gpt-5-mini_by_default');
        }
      },

      onSources: (data) => {
        sources = data.sources;
        const sudachiInfo = (data as any)?.sudachi;
        if (sudachiInfo && typeof sudachiInfo === 'object') {
          sudachiUsed = !!sudachiInfo.used;
          if (Array.isArray(sudachiInfo.keywords) && sudachiInfo.keywords.length > 0) {
            sudachiKeywords = sudachiInfo.keywords as string[];
          }
        }
        setStreamingProgress(data.progress);
      },

      onChunk: (data) => {
        currentContent = data.fullContent;
        setStreamingProgress(data.progress);

        const updateMessage = () => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === messageId ? {
                ...msg,
                content: currentContent,
                isLoading: true
              } : msg
            )
          );
        };

        if (rafId == null) {
          rafId = window.requestAnimationFrame(() => {
            rafId = null;
            updateMessage();
          });
        }
      },

      onAnswer: (data) => {
        // 進捗（未指定なら据え置き）
        if (typeof (data as any)?.progress === 'number') {
          setStreamingProgress((data as any).progress);
        }

        // ストリーム中は追記、完了イベント(isComplete)では内容を上書きしない
        if (!data.isComplete && data.content) {
          currentContent += data.content;

          // 逐次描画（rafで集約）
          const updateMessage = () => {
            setMessages(prev => 
              prev.map(msg => 
                msg.id === messageId ? {
                  ...msg,
                  content: currentContent,
                  isLoading: true
                } : msg
              )
            );
          };
          if (rafId == null) {
            rafId = window.requestAnimationFrame(() => {
              rafId = null;
              updateMessage();
            });
          }
        }

        // 最終指標は保持（内容はonCompleteで確定）
        if (data.isComplete) {
          // 検索結果0件などのガードレール時、サーバーは chunk を一切送らず
          // isComplete=true の answer 1発でフォールバック本文を渡してくる。
          // currentContent が空のときは、その本文をそのまま採用して表示する。
          const guardrailsTriggered = !!(data as any)?.guardrails?.triggered;
          if (data.content && (currentContent.length === 0 || guardrailsTriggered)) {
            currentContent = data.content;
            setMessages(prev =>
              prev.map(msg =>
                msg.id === messageId ? {
                  ...msg,
                  content: currentContent,
                  isLoading: true,
                } : msg
              )
            );
          }

          // ✅ 案 A: 補足回答が続く場合、UI に「補足生成中…」を出させるため refinedStatus='pending' をセット
          if ((data as any)?.auxRefinePending === true) {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === messageId ? { ...msg, refinedStatus: 'pending' } : msg
              )
            );
          }

          finalRagResponse = {
            success: true,
            answer: currentContent,
            sources: sources,
            confidence: data.confidence ?? 0,
            processingTime: 0,
            usage: {
              vectorSearchTime: 0,
              llmGenerationTime: 0,
              totalTokens: (data as any).tokensUsed ?? (data as any).totalTokens ?? 0,
              estimatedCost: 0
            },
            guardrails: {
              triggered: guardrailsTriggered,
              reason: (data as any)?.guardrails?.reason,
              fallbackUsed: guardrailsTriggered,
            },
            metadata: {
              queryHash: '',
              model: 'gpt-4.1-nano',
              searchResults: sources.length,
              contextLength: 0,
              normalizedQuery: normalizedText || query,
              originalQuery: query
            },
            sudachi: { used: sudachiUsed, keywords: Array.isArray(sudachiKeywords) ? sudachiKeywords : [] }
          } as any;
        }
      },

      onComplete: (data) => {
        setStreamingStatus(uiText("Complete"));
        setStreamingProgress(100);
        
        // ローディング状態を解除
        setIsLoading(false);

        // 保留のフレーム更新があればキャンセル
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (finalRagResponse) {
          finalRagResponse.processingTime = data.processingTime;
          finalRagResponse.usage.totalTokens = (data as any).tokensUsed ?? (data as any).totalTokens ?? finalRagResponse.usage.totalTokens;
          finalRagResponse.confidence = (data as any).confidence ?? finalRagResponse.confidence ?? 0;
        }

        // 最終メッセージ更新（refined* 系フィールドは既存値を保持し、pending のままなら failed にフォールバック）
        setMessages(prev =>
          prev.map(msg => {
            if (msg.id !== messageId) return msg;
            const stillPending = msg.refinedStatus === 'pending';
            return {
              ...msg,
              type: 'assistant',
              content: currentContent,
              timestamp: new Date(),
              ragResponse: finalRagResponse,
              isLoading: false,
              mode: searchMode,
              ...(stillPending
                ? { refinedStatus: 'failed' as const, refinedFailureMessage: AUX_REFINED_FAILURE_MESSAGE }
                : {}),
            };
          })
        );

        // ストリーミング状態もリセット
        setTimeout(() => {
          setStreamingProgress(0);
          setStreamingStatus('');
        }, 1000);
      },

      onSessionTitle: (data) => {
        if (data.title) {
          setCurrentSessionTitle(data.title);
          if (pendingSessionTitleSessionIdRef.current === data.sessionId) {
            clearPendingSessionTitleTimeout();
          }
          onSessionHistoryTitleResolved?.({
            sessionId: data.sessionId,
            title: data.title,
          });
          setSessionHistory(prev => {
            const exists = prev.some(e => e.sessionId === data.sessionId);
            if (!exists) return prev;
            return prev.map(e =>
              e.sessionId === data.sessionId ? { ...e, title: data.title } : e
            );
          });
        }
      },

      onFreshness: (data) => {
        if (!data || !Array.isArray(data.checks) || data.checks.length === 0) return;
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId ? { ...msg, freshness: data.checks as FreshnessCheckItem[] } : msg
          )
        );
      },

      onAnswerRefined: (data) => {
        // ✅ 案 A: 参照 URL 補助データ 2-pass の結果を「補足セクション」として追加表示
        //  - 初回回答（content）は差替えない
        //  - status='ready': refinedContent にフル補足回答を入れる
        //  - status='failed': refinedFailureMessage に固定文言を入れる
        if (!data) return;
        const isReady = data.status === 'ready' && typeof data.content === 'string' && data.content.length > 0;
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  refinedStatus: isReady ? 'ready' : 'failed',
                  ...(isReady ? { refinedContent: data.content } : { refinedContent: undefined }),
                  ...(isReady ? {} : { refinedFailureMessage: data.failureMessage || AUX_REFINED_FAILURE_MESSAGE }),
                  refinedFromUrls: Array.isArray(data.refinedFromUrls) && data.refinedFromUrls.length > 0
                    ? data.refinedFromUrls
                    : msg.refinedFromUrls,
                }
              : msg
          )
        );
      },

      onError: (data) => {
        console.error('❌ [Streaming] エラー:', data);
        setStreamingStatus(uiText("Error: {{message}}", { message: data.message }));
        terminalError = createRagHandledError(data.code, data.message);
        
        // ローディング状態を解除
        setIsLoading(false);
        
        const errorMessage: ChatMessage = {
          id: messageId,
          type: 'assistant',
          content: getBilingualRagErrorMessage(data.code),
          timestamp: new Date(),
          isLoading: false
        };

        setMessages(prev => 
          prev.map(msg => 
            msg.id === messageId ? errorMessage : msg
          )
        );

        // ストリーミング状態もリセット
        setTimeout(() => {
          setStreamingProgress(0);
          setStreamingStatus('');
        }, 2000);

        onError?.(data.message);
      }
    });

    if (terminalError) {
      throw terminalError;
    }
  };

  /**
   * Enterキー処理
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !shouldBlockEnterSubmit(e)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * 信頼度に基づく色の取得
   */
  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  /**
   * 類似度に基づく色の取得
   */
  const getSimilarityColor = (score: number): string => {
    if (score >= 0.8) return 'bg-green-100 text-green-800';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  // ワークスペースが選択されていない場合の表示
  if (!workspaceId) {
    return (
      <div className={`rag-search-interface ${className}`}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '400px',
          color: '#666',
          fontSize: '16px'
        }}>
          <div style={{ opacity: 0.5 }}>
            <SearchIcon className="w-12 h-12 mb-4" />
          </div>
          <p>{uiText("Please select a workspace")}</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            {uiText("A Workspace is required to use AI Chat")}
          </p>
        </div>
      </div>
    );
  }

  // 初期レイアウト判定（まだ一度も送信しておらず、履歴が空）
  const isInitialLayout = !hasSentFirstMessage && messages.length === 0;

  return (
    <div className={`rag-search-interface ${className} ${isInitialLayout ? 'initial-layout' : ''}`}>
      {showReferencedUrlFollowUp && (
        <div className="rag-aux-url-toggle">
          <div className="rag-aux-url-toggle-row">
            <label>{uiText("Read referenced links and add a follow-up answer")}</label>
            <div className="rag-aux-url-toggle-control">
              <span className={crawlReferencedUrls ? 'on' : 'off'}>
                {crawlReferencedUrls ? 'ON' : 'OFF'}
              </span>
              <button
                type="button"
                onClick={() => void handleCrawlReferencedUrlsToggle()}
                disabled={crawlReferencedUrlsSaving}
                aria-pressed={crawlReferencedUrls}
                aria-label={uiText("Read referenced links and add a follow-up answer")}
              >
                <span
                  className="rag-aux-url-toggle-knob"
                  style={{ left: crawlReferencedUrls ? '26px' : '2px' }}
                />
              </button>
            </div>
          </div>
          <p>{uiText("Fetches the content of external URLs in the answer and adds a follow-up after the first answer")}</p>
        </div>
      )}
      {/* ヘッダー - ファーストリリースでは非表示 */}
      {/* <div className="rag-header">
        <div className="rag-header-title">
          <SearchIcon className="w-5 h-5" />
          <h2>AIチャット</h2>
        </div>
        
        <div className="rag-header-controls">
          <div className="mode-toggle">
            <button
              className={`mode-button ${searchMode === 'answer' ? 'active' : ''}`}
              onClick={() => setSearchMode('answer')}
            >
              チャット
            </button>
            <button
              className={`mode-button ${searchMode === 'search' ? 'active' : ''}`}
              onClick={() => setSearchMode('search')}
            >
              検索
            </button>
          </div>

          <button
            className={`menu-toggle-button ${showMenuOptions ? 'active' : ''}`}
            onClick={() => setShowMenuOptions(!showMenuOptions)}
            title="メニューの表示切り替え"
            aria-label={`${showMenuOptions ? 'メニューを非表示にする' : 'メニューを表示する'}`}
            aria-pressed={showMenuOptions}
          >
            ⚙️
          </button>

          {showMenuOptions && (
            <div className="display-options">
            <button
              className={`option-button streaming-toggle ${useStreaming ? 'active' : ''}`}
              onClick={() => setUseStreaming(!useStreaming)}
              title="ストリーミング切り替え"
              aria-label={`${useStreaming ? 'ストリーミングモードを無効にする' : 'ストリーミングモードを有効にする'}`}
              aria-pressed={useStreaming}
            >
              📡 {useStreaming ? 'ストリーミング' : '通常'}
            </button>
            <button
              className={`option-button ${showSources ? 'active' : ''}`}
              onClick={() => setShowSources(!showSources)}
              title="情報源を表示"
            >
              情報源
            </button>
            <button
              className={`option-button ${showMetrics ? 'active' : ''}`}
              onClick={() => setShowMetrics(!showMetrics)}
              title="メトリクスを表示"
            >
              詳細
            </button>
            <button
              className="option-button debug-button"
              onClick={handleDebugEmbedding}
              disabled={isLoading}
              title="embeddingデータを確認"
            >
              🔍 デバッグ
            </button>
            <button
              className="option-button debug-button"
              onClick={handleVectorSearchTest}
              disabled={isLoading}
              title="Vector Searchテスト"
            >
              🧪 Vector Test
            </button>
            </div>
          )}
        </div>
      </div> */}
      {/* Vector Test結果表示 */}
      {showVectorTest && vectorTestResult && (
        <div className="debug-panel">
          <div className="debug-header">
            <h3>🧪 Vector Search Test Results</h3>
            <button onClick={() => setShowVectorTest(false)}>✕</button>
          </div>
          <div className="debug-content">
            <h4>Status: {vectorTestResult.success ? '✅ Success' : '❌ Failed'}</h4>
            
            <h4>📊 Normal Query Results:</h4>
            <p>Documents found: {vectorTestResult.normalQueryResults?.count || 0}</p>
            {vectorTestResult.normalQueryResults?.documents?.map((doc: any, index: number) => (
              <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd' }}>
                <strong>Doc {index + 1}:</strong> {doc.chunkId}<br/>
                <strong>Workspace:</strong> {doc.workspaceId}<br/>
                <strong>Embedding Field:</strong> {doc.hasEmbeddingField ? '✅' : '❌'} ({doc.embeddingLength} dims)<br/>
                <strong>Text:</strong> {doc.textPreview}
              </div>
            ))}
            
            {vectorTestResult.vectorSearchResults && (
              <>
                <h4>🎯 Vector Search Results:</h4>
                <p>Documents found: {vectorTestResult.vectorSearchResults.count || 0}</p>
                {vectorTestResult.vectorSearchResults.documents?.map((doc: any, index: number) => (
                  <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd' }}>
                    <strong>Result {index + 1}:</strong> {doc.chunkId}<br/>
                    <strong>Distance:</strong> {doc.distance}<br/>
                    <strong>Similarity:</strong> {doc.similarityScore}<br/>
                    <strong>Text:</strong> {doc.textPreview}
                  </div>
                ))}
              </>
            )}
            
            {vectorTestResult.vectorSearchError && (
              <>
                <h4>❌ Vector Search Error:</h4>
                <p>{vectorTestResult.vectorSearchError.message}</p>
                <p>Code: {vectorTestResult.vectorSearchError.code}</p>
              </>
            )}
            
            {vectorTestResult.error && (
              <>
                <h4>❌ Error:</h4>
                <p>{vectorTestResult.error}</p>
              </>
            )}
          </div>
        </div>
      )}
      {/* デバッグ情報表示 */}
      {showDebug && debugInfo && (
        <div className="debug-panel">
          <div className="debug-header">
            <h3>{uiText("🔍 Embedding data diagnostics")}</h3>
            <button 
              className="close-debug"
              onClick={() => setShowDebug(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="debug-content">
            <div className="debug-section">
              <h4>{uiText("Collection group query results")}</h4>
              <p>{uiText("Workspace '")}{debugInfo.workspaceId}{uiText("': documents found:")} <strong>{debugInfo.collectionGroupResults.count}{uiText(" items")}</strong></p>
              
              {debugInfo.collectionGroupResults.documents.map((doc: any, index: number) => (
                <div key={doc.docId} className="debug-doc">
                  <div className="debug-doc-header">
                    <span className="doc-index">#{index + 1}</span>
                    <span className="doc-id">{doc.chunkId}</span>
                  </div>
                  <div className="debug-doc-details">
                    <div className="debug-row">
                      <span className="label">Path:</span>
                      <span className="value">{doc.path}</span>
                    </div>
                    <div className="debug-row">
                      <span className="label">Board ID:</span>
                      <span className="value">{doc.boardId}</span>
                    </div>
                    <div className="debug-row">
                      <span className="label">Embedding:</span>
                      <span className={`value ${doc.hasEmbedding ? 'success' : 'error'}`}>
                        {doc.hasEmbedding ? uiText("✅ Yes ({{embeddingLength}} dimensions)", { embeddingLength: doc.embeddingLength }) : uiText("❌ None")}
                      </span>
                    </div>
                    <div className="debug-row">
                      <span className="label">Text:</span>
                      <span className="value text-preview">{doc.textPreview}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="debug-section">
              <h4>{uiText("Query results at the workspace root")}</h4>
              <p>{uiText("Documents found:")} <strong>{debugInfo.workspaceDirectResults.count}{uiText(" items")}</strong></p>
              
              {debugInfo.workspaceDirectResults.documents.map((doc: any, index: number) => (
                <div key={doc.docId} className="debug-doc">
                  <div className="debug-doc-header">
                    <span className="doc-index">#{index + 1}</span>
                    <span className="doc-id">{doc.chunkId}</span>
                  </div>
                  <div className="debug-doc-details">
                    <div className="debug-row">
                      <span className="label">Path:</span>
                      <span className="value">{doc.path}</span>
                    </div>
                    <div className="debug-row">
                      <span className="label">Embedding:</span>
                      <span className={`value ${doc.hasEmbedding ? 'success' : 'error'}`}>
                        {doc.hasEmbedding ? uiText("✅ Yes ({{embeddingLength}} dimensions)", { embeddingLength: doc.embeddingLength }) : uiText("❌ None")}
                      </span>
                    </div>
                    <div className="debug-row">
                      <span className="label">Text:</span>
                      <span className="value text-preview">{doc.textPreview}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* メッセージエリア */}
      <div className="rag-messages">
        {messages.length === 0 && (
          <div className="rag-welcome">
            <div className="welcome-icon">
              <SearchIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3>{uiText("Welcome to notreu AI Chat")}</h3>
            <p>
              {uiText("Search the page list in the workspace, ")}
              <br />{uiText("notreu AI Chat will answer.")}
              <br />{uiText("Enter a question.")}
              <br />{uiText("※ Files, threads, and similar data are not included.")}
            </p>
            <div className="welcome-examples" style={{ display: 'none' }}>
              <p className="examples-title">{uiText("For example:")}</p>
              <button 
                className="example-query"
                onClick={() => setInputValue(uiText("How is the project going?"))}
              >
                {uiText("\"How is the project going?\"")}
              </button>
              <button 
                className="example-query"
                onClick={() => setInputValue(uiText("Show me the minutes of the latest meeting"))}
              >
                {uiText("\"Show me the notes from the latest meeting\"")}
              </button>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.type} ${message.isLoading && useStreaming ? 'streaming' : ''}`}
          >
            {/* ユーザーメッセージ */}
            {message.type === 'user' && (
              <div className="message-content user-message">
                <div className="message-text">{message.content}</div>
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            )}

            {/* アシスタントメッセージ */}
            {message.type === 'assistant' && (
              <div className="message-content assistant-message">
                {/* ローディング状態 */}
                {message.isLoading && (
                  <div className="loading-indicator">
                    <LoaderIcon className="w-4 h-4 animate-spin" />
                    <span>{message.mode === 'search' ? uiText("Searching related pages...") : 'Answering...'}</span>
                  </div>
                )}

                {/* 検索モードラベル */}
                {/* 回答内容（isLoading中も表示） */}
                {message.content && (
                  <div
                    className="message-text"
                    aria-live={message.isLoading ? 'polite' : undefined}
                    aria-atomic={message.isLoading ? 'false' : undefined}
                  >
                    {renderWithLinks(message.content)}
                  </div>
                )}

                {/* ✅ 案 A: 参照 URL 補助データ 2-pass の補足セクション（追加型 UX）
                 *  デザイン方針:
                 *   - 薄いグレー基調（一次回答よりも一段トーンを落とし「補足」であることを視覚的に示す）
                 *   - スピナーは細いリングを 10px で控えめに（既存 spin keyframe を流用）
                 *   - ヘッダーは小型 + letter-spacing + 小ドットで繊細に
                 *   - fadeIn で優しく現れる
                 */}
                {message.refinedStatus === 'pending' && (
                  <div
                    className="rag-answer-refined-pending"
                    role="status"
                    aria-live="polite"
                    style={{
                      marginTop: '10px',
                      padding: '10px 14px',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #eef1f5',
                      color: '#64748b',
                      fontSize: '12px',
                      lineHeight: 1.55,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      animation: 'fadeIn 0.3s ease-out',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        border: '1.5px solid #e2e8f0',
                        borderTopColor: '#94a3b8',
                        borderRadius: '50%',
                        animation: 'spin 0.9s linear infinite',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ letterSpacing: '0.01em' }}>
                      {uiText("Generating a follow-up answer from referenced URLs")}
                    </span>
                  </div>
                )}
                {message.refinedStatus === 'ready' && typeof message.refinedContent === 'string' && message.refinedContent.length > 0 && (
                  <div
                    className="rag-answer-refined-section"
                    style={{
                      marginTop: '12px',
                      padding: '14px 16px',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #eef1f5',
                      borderLeft: '2px solid #cbd5e1',
                      color: '#1f2937',
                      animation: 'fadeIn 0.3s ease-out',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#94a3b8',
                        marginBottom: '10px',
                        letterSpacing: '0.04em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block',
                          width: '4px',
                          height: '4px',
                          borderRadius: '50%',
                          backgroundColor: '#cbd5e1',
                        }}
                      />
                      {uiText("Follow-up answer including referenced URL content")}
                      {Array.isArray(message.refinedFromUrls) && message.refinedFromUrls.length > 0
                        ? uiText(" · {{length}} items", { length: message.refinedFromUrls.length })
                        : ''}
                    </div>
                    <div className="message-text">
                      {renderWithLinks(message.refinedContent)}
                    </div>
                    {Array.isArray(message.refinedFromUrls) && message.refinedFromUrls.length > 0 && (
                      <ul
                        style={{
                          marginTop: '12px',
                          marginBottom: 0,
                          paddingLeft: 0,
                          listStyle: 'none',
                          fontSize: '11px',
                          color: '#94a3b8',
                          borderTop: '1px solid #eef1f5',
                          paddingTop: '10px',
                        }}
                      >
                        {message.refinedFromUrls.slice(0, 5).map((u, i) => (
                          <li
                            key={i}
                            style={{
                              wordBreak: 'break-all',
                              marginBottom: '3px',
                            }}
                          >
                            {u}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {message.refinedStatus === 'failed' && (
                  <div
                    className="rag-answer-refined-failed"
                    role="status"
                    aria-live="polite"
                    style={{
                      marginTop: '10px',
                      padding: '10px 14px',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #eef1f5',
                      color: '#94a3b8',
                      fontSize: '12px',
                      lineHeight: 1.55,
                      animation: 'fadeIn 0.3s ease-out',
                    }}
                  >
                    {uiText(message.refinedFailureMessage || AUX_REFINED_FAILURE_MESSAGE)}
                  </div>
                )}

                {/* 参照 URL 鮮度チェック: stale のみバナー表示（fresh/unknown は表示しない） */}
                {!message.isLoading && Array.isArray(message.freshness) && message.freshness.some(c => c.status === 'stale') && (
                  <div
                    className="rag-freshness-banner"
                    role="status"
                    aria-live="polite"
                    style={{
                      marginTop: '10px',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#fff7ed',
                      border: '1px solid #fed7aa',
                      color: '#7c2d12',
                      fontSize: '13px',
                      lineHeight: 1.55,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {uiText("The linked page may have been updated")}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '4px' }}>
                      {uiText("Getting the latest information…")}
                    </div>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
                      {message.freshness.filter(c => c.status === 'stale').map((c) => (
                        <li key={c.url} style={{ wordBreak: 'break-all' }}>
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#9a3412', textDecoration: 'underline' }}
                          >
                            {c.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 詳細は完了後のみ表示 */}
                {!message.isLoading && (
                  <>
                    {/* RAG応答の詳細情報 */}
                    {message.ragResponse && (
                      <div className="rag-details">
                        {/* 信頼度とメトリクス - ファーストリリースでは信頼度を非表示 */}
                        <div className="rag-metrics">
                          {/* <div className="metric">
                            <CheckIcon className="w-4 h-4" />
                            <span className={getConfidenceColor(message.ragResponse?.confidence ?? 0)}>
                              信頼度: {Number.isFinite((message.ragResponse?.confidence ?? 0) as any) ? ((message.ragResponse?.confidence ?? 0) * 100).toFixed(1) : '0.0'}%
                            </span>
                          </div> */}
                          
                          {showMetrics && (
                            <>
                              <div className="metric">
                                <ClockIcon className="w-4 h-4" />
                                <span>{message.ragResponse.processingTime}ms</span>
                              </div>
                              <div className="metric">
                                <DollarIcon className="w-4 h-4" />
                                <span>${(message.ragResponse?.usage?.estimatedCost ?? 0).toFixed(6)}</span>
                              </div>
                            </>
                          )}

                        </div>

                        {/* 情報源 */}
                        {showSources && message.ragResponse.sources.length > 0 && (
                          <div className="rag-sources" role="region" aria-labelledby={`sources-title-${message.id}`}>
                            <h4 id={`sources-title-${message.id}`}>{uiText("Information source:")}</h4>
                            <div className="sources-list" role="list">
                              {message.ragResponse.sources.map((source, index) => (
                                <div
                                  key={`${source.boardId}-${source.chunkId}`}
                                  className="source-item"
                                  role="listitem"
                                  tabIndex={0}
                                  aria-label={uiText("Source {{v0}}, similarity {{v1}}%{{v2}}", { v0: index + 1, v1: Number.isFinite(source?.similarityScore as any) ? (source.similarityScore * 100).toFixed(1) : '0.0', v2: source.metadata?.title ? uiText(", title {{title}}", { title: source.metadata.title }) : '' })}
                                >
                                  <div className="source-header">
                                    <span className="source-index">#{index + 1}</span>
                                    <span className={`similarity-score ${getSimilarityColor(source?.similarityScore ?? 0)}`}>
                                      {Number.isFinite(source?.similarityScore as any) ? (source.similarityScore * 100).toFixed(1) : '0.0'}%
                                    </span>
                                    {source.metadata?.title && (
                                      <span className="source-title">{source.metadata.title}</span>
                                    )}
                                  </div>
                                  <div className="source-text">
                                    {renderHighlighted(
                                      source.text,
                                      [
                                        ...(((message.ragResponse as any)?.sudachi?.keywords as string[]) || []),
                                        ...String(((message.ragResponse as any)?.metadata?.normalizedQuery) || '')
                                          .split(/[\s\u3000,，、。・/\\\-]+/)
                                      ],
                                      200
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 詳細メトリクス */}
                        {showMetrics && (
                          <div className="detailed-metrics">
                            <div className="metrics-grid">
                              <div className="metric-item">
                                <span className="metric-label">{uiText("Search time:")}</span>
                                <span className="metric-value">{message.ragResponse.usage.vectorSearchTime}ms</span>
                              </div>
                              <div className="metric-item">
                                <span className="metric-label">{uiText("Generation time:")}</span>
                                <span className="metric-value">{message.ragResponse.usage.llmGenerationTime}ms</span>
                              </div>
                              <div className="metric-item">
                                <span className="metric-label">{uiText("Token count:")}</span>
                                <span className="metric-value">{message.ragResponse.usage.totalTokens}</span>
                              </div>
                              <div className="metric-item">
                                <span className="metric-label">{uiText("Results:")}</span>
                                <span className="metric-value">{message.ragResponse.metadata.searchResults}{uiText(" items")}</span>
                              </div>
                              {/* モデル名はユーザーに表示しない */}
                              <div className="metric-item">
                                <span className="metric-label">{uiText("Context length:")}</span>
                                <span className="metric-value">{message.ragResponse.metadata.contextLength}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="message-timestamp">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>
      {/* 入力エリア */}
      <div className="rag-input-area">
        {isInitialLayout && (
          <div className="initial-greeting">
            <h3>{uiText("Welcome to notreu AI Chat")}</h3>
          </div>
        )}
        {isInitialLayout && (
          <div className="initial-description">
            {uiText("Search the page list in the workspace, ")}
            <br />{uiText("notreu AI Chat will answer.")}
            <br />{uiText("Enter a question.")}
            <br />{uiText("※ Files, threads, and similar data are not included.")}
          </div>
        )}
        {/* ストリーミングプログレス表示（公開版と同様: 入力欄の上にバーのみ） */}
        {isLoading && useStreaming && (
          <div
            className="streaming-progress-container"
            role="progressbar"
            aria-valuenow={streamingProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={uiText("AI answer generation progress")}
            aria-valuetext={streamingStatus || uiText("Generating AI answer...")}
          >
            <div className="streaming-progress-bar" style={{ width: `${streamingProgress}%` }}></div>
          </div>
        )}
        <div className="input-container">
          <div className="input-wrapper">
            {isRecording && !isTranscribing && (
              <MicFlowVisualizer active={isRecording && !isTranscribing} levelRef={micLevelRef} />
            )}
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={onCompositionEnd}
              onKeyDown={handleKeyPress}
              placeholder={
                isRecording ? '' : (searchMode === 'answer'
                  ? uiText("Enter a message...")
                  : uiText("Search pages or topics..."))
              }
              style={{ color: isRecording ? '#94a3b8' : undefined }}
              className="rag-input"
              rows={1}
              disabled={isLoading}
            />
          </div>
          <div className="input-bottom-row">
            <button
              type="button"
              className="input-plus-button"
              title={uiText("Add")}
              disabled={isLoading}
            >
              <PlusIcon className="w-5 h-5" />
            </button>
            <div className="input-bottom-right">
            <div className="admin-rag-mode-dropdown" ref={modeDropdownRef}>

              <button
                type="button"
                className="admin-rag-mode-dropdown-trigger"
                onClick={() => {
                  if (isLoading) return;
                  if (!modeDropdownOpen && modeDropdownRef.current) {
                    const rect = modeDropdownRef.current.getBoundingClientRect();
                    const spaceAbove = rect.top;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    setDropdownDirection(spaceAbove >= spaceBelow ? 'up' : 'down');
                  }
                  setModeDropdownOpen(prev => !prev);
                }}
                disabled={isLoading}
              >
                <DelayedTooltip
                  content={
                    searchMode === 'answer'
                      ? <div>{uiText("AI answers questions using information from the pages")}</div>
                      : <div>{uiText("AI finds related pages and suggests them")}</div>
                  }
                  placement="top"
                >
                  <span className="admin-rag-mode-dropdown-label">
                    {searchMode === 'answer' ? uiText("Answer") : uiText("Search")}
                  </span>
                </DelayedTooltip>
                <svg className={`admin-rag-mode-dropdown-arrow ${modeDropdownOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              {modeDropdownOpen && (
                <div className={`admin-rag-mode-dropdown-menu dropdown-${dropdownDirection}`}>
                  <button
                    type="button"
                    className={`admin-rag-mode-dropdown-item ${searchMode === 'answer' ? 'selected' : ''}`}
                    onClick={() => { setSearchMode('answer'); setModeDropdownOpen(false); }}
                  >
                    <div className="admin-rag-mode-dropdown-item-text">
                      <span className="admin-rag-mode-dropdown-item-title">{uiText("Answer")}</span>
                      <span className="admin-rag-mode-dropdown-item-desc">{uiText("AI answers questions using information from the pages")}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`admin-rag-mode-dropdown-item ${searchMode === 'search' ? 'selected' : ''}`}
                    onClick={() => { setSearchMode('search'); setModeDropdownOpen(false); }}
                  >
                    <div className="admin-rag-mode-dropdown-item-text">
                      <span className="admin-rag-mode-dropdown-item-title">{uiText("Search")}</span>
                      <span className="admin-rag-mode-dropdown-item-desc">{uiText("AI finds related pages and suggests them")}</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => isRecording ? stopWhisper() : startWhisper()}
              disabled={isLoading || isTranscribing}
              className={`mic-button ${isRecording ? 'recording' : ''}`}
              title={isRecording ? uiText("Stop recording") : uiText("Voice input")}
              aria-label={isRecording ? uiText("Stop recording") : uiText("Voice input")}
              aria-pressed={isRecording}
            >
              {isTranscribing ? (
                <LoaderIcon className="w-5 h-5 animate-spin" />
              ) : isRecording ? (
                <StopIcon className="w-5 h-5" />
              ) : (
                <MicIcon className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
              title={uiText("Send (Enter)")}
            >
              {isLoading ? (
                <LoaderIcon className="w-5 h-5 animate-spin" />
              ) : (
                <SendIcon className="w-5 h-5" />
              )}
            </button>
            </div>
          </div>
        </div>

        {/* 入力ヒント */}
        <div className="input-hints">
          <div className="hint-text">
            <span className="hint">
              {searchMode === 'answer' ? uiText("Shift+Enter for a new line") : uiText("Press Enter to search")}
            </span>
            <span className="disclaimer">
              {uiText("AI Chat answers are not always correct. Please check the actual page for important information.")}
            </span>
          </div>
          {isLoading && (
            <span className="processing-hint">
              Answering...
            </span>
          )}
          {sttError && (
            <span className="processing-hint" style={{ color: '#ef4444' }}>
              {sttError}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RAGSearchInterface;
